import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Loader2, Settings, Users, Tag, Building2, Bot, Calendar, Save, Shield, GitBranch, ScrollText, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatDateShort } from '@/lib/utils';
import Roles from './Roles';
import Workflows from './Workflows';
import AuditLog from './AuditLog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Admin() {
  const { user, subscriptionStatus } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [structure, setStructure] = useState({});
  const [exercice, setExercice] = useState(null);
  const [aiConfig, setAiConfig] = useState({});
  const [invoiceTemplate, setInvoiceTemplate] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Dialogs
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ email: '', password: '', nom: '', prenom: '', role: 'comptable' });
  const [newCat, setNewCat] = useState({ code: '', label: '' });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [uRes, cRes, sRes, eRes, rRes, aiRes, tplRes] = await Promise.all([
          axios.get(`${API_URL}/structure/users`),
          axios.get(`${API_URL}/suppliers/categories`).catch(() => ({ data: { categories: [] } })),
          axios.get(`${API_URL}/structure/info`).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/comptabilite/exercices/current`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/roles/`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/structure/ai-config`).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/structure/invoice-template`).catch(() => ({ data: {} })),
        ]);
        setUsers(uRes.data);
        setCategories(cRes.data.categories || []);
        setStructure(sRes.data);
        setExercice(eRes.data);
        setRoles(rRes.data);
        setAiConfig(aiRes.data);
        setInvoiceTemplate(tplRes.data);
      } catch (e) {} finally { setLoading(false); }
    };
    fetch();
  }, []);

  // ===== Structure =====
  const saveStructure = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/structure/info`, structure);
      toast.success('Informations mises à jour');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // ===== Invoice Template =====
  const saveTemplate = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/structure/invoice-template`, invoiceTemplate);
      setInvoiceTemplate(res.data);
      toast.success('Template facture mis à jour');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // ===== AI Config =====
  const saveAi = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/structure/ai-config`, aiConfig);
      toast.success('Configuration IA mise à jour');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // ===== Users =====
  const saveUser = async () => {
    if (!userForm.email || !userForm.nom || !userForm.prenom) { toast.error('Champs requis'); return; }
    if (!editingUser && !userForm.password) { toast.error('Mot de passe requis'); return; }
    setSaving(true);
    try {
      if (editingUser) { await axios.put(`${API_URL}/structure/users/${editingUser}`, userForm); toast.success('Utilisateur modifié'); }
      else { await axios.post(`${API_URL}/structure/users`, userForm); toast.success('Utilisateur créé'); }
      setUserDialogOpen(false); setEditingUser(null);
      setUsers((await axios.get(`${API_URL}/structure/users`)).data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Désactiver cet utilisateur ?')) return;
    try {
      await axios.delete(`${API_URL}/structure/users/${id}`);
      toast.success('Utilisateur désactivé');
      setUsers((await axios.get(`${API_URL}/structure/users`)).data);
    } catch (e) { toast.error('Erreur'); }
  };

  // ===== Categories =====
  const saveCategories = async () => {
    try { await axios.put(`${API_URL}/suppliers/categories`, { categories }); toast.success('Catégories sauvegardées'); }
    catch (e) { toast.error('Erreur'); }
  };
  const addCategory = () => {
    if (!newCat.code || !newCat.label) { toast.error('Code et label requis'); return; }
    setCategories([...categories, newCat]); setNewCat({ code: '', label: '' }); setCatDialogOpen(false);
  };
  const removeCategory = (code) => setCategories(categories.filter(c => c.code !== code));

  // Role options for user form
  const roleOptions = [
    ...roles.filter(r => r.is_system).map(r => ({ value: r.code, label: r.nom })),
    ...roles.filter(r => !r.is_system).map(r => ({ value: r.code, label: `${r.nom} (custom)` })),
  ];

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold"><Settings className="w-6 h-6 inline mr-2" />Paramètres</h1>

      <Tabs defaultValue="structure">
        <TabsList className="flex-wrap">
          <TabsTrigger value="structure"><Building2 className="w-4 h-4 mr-1" /> Structure</TabsTrigger>
          <TabsTrigger value="users"><Users className="w-4 h-4 mr-1" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="roles"><Shield className="w-4 h-4 mr-1" /> Rôles</TabsTrigger>
          <TabsTrigger value="workflows"><GitBranch className="w-4 h-4 mr-1" /> Workflows</TabsTrigger>
          <TabsTrigger value="exercice"><Calendar className="w-4 h-4 mr-1" /> Exercice</TabsTrigger>
          <TabsTrigger value="ai"><Bot className="w-4 h-4 mr-1" /> IA</TabsTrigger>
          <TabsTrigger value="categories"><Tag className="w-4 h-4 mr-1" /> Catégories</TabsTrigger>
          <TabsTrigger value="template"><FileText className="w-4 h-4 mr-1" /> Template facture</TabsTrigger>
          <TabsTrigger value="audit"><ScrollText className="w-4 h-4 mr-1" /> Audit</TabsTrigger>
        </TabsList>

        {/* ===== STRUCTURE ===== */}
        <TabsContent value="structure">
          <Card>
            <CardHeader>
              <CardTitle><Building2 className="w-5 h-5 inline mr-2" />Informations de la structure</CardTitle>
              <CardDescription>Ces informations apparaissent sur vos factures et documents.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nom / Raison sociale</Label><Input value={structure.nom || ''} onChange={e => setStructure({...structure, nom: e.target.value})} /></div>
                <div><Label>SIRET</Label><Input value={structure.siret || ''} onChange={e => setStructure({...structure, siret: e.target.value})} /></div>
                <div className="col-span-2"><Label>Adresse</Label><Input value={structure.adresse || ''} onChange={e => setStructure({...structure, adresse: e.target.value})} /></div>
                <div><Label>Code postal</Label><Input value={structure.code_postal || ''} onChange={e => setStructure({...structure, code_postal: e.target.value})} /></div>
                <div><Label>Ville</Label><Input value={structure.ville || ''} onChange={e => setStructure({...structure, ville: e.target.value})} /></div>
                <div><Label>Téléphone</Label><Input value={structure.telephone || ''} onChange={e => setStructure({...structure, telephone: e.target.value})} /></div>
                <div><Label>Email</Label><Input value={structure.email || ''} onChange={e => setStructure({...structure, email: e.target.value})} /></div>
              </div>
              <Button onClick={saveStructure} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
                <Save className="w-4 h-4 mr-2" /> Enregistrer
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== UTILISATEURS ===== */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{users.length} utilisateur(s) — {users.filter(u => u.is_active).length} actif(s)</p>
            <Button onClick={() => { setEditingUser(null); setUserForm({ email: '', password: '', nom: '', prenom: '', role: 'comptable' }); setUserDialogOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
              <Plus className="w-4 h-4 mr-2" /> Nouvel utilisateur
            </Button>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow>
              <TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Statut</TableHead><TableHead className="w-24">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{users.map(u => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.prenom} {u.nom}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell><Badge variant="outline">{roles.find(r => r.code === u.role)?.nom || u.role}</Badge></TableCell>
                <TableCell><Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{u.is_active ? 'Actif' : 'Inactif'}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingUser(u.id); setUserForm({ email: u.email, password: '', nom: u.nom, prenom: u.prenom, role: u.role }); setUserDialogOpen(true); }}><Edit className="w-4 h-4" /></Button>
                    {u.id !== user?.id && <Button variant="ghost" size="sm" onClick={() => deleteUser(u.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* ===== EXERCICE ===== */}
        <TabsContent value="exercice">
          <Card>
            <CardHeader>
              <CardTitle><Calendar className="w-5 h-5 inline mr-2" />Exercice comptable en cours</CardTitle>
            </CardHeader>
            <CardContent>
              {exercice ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Label</p>
                      <p className="text-lg font-bold">{exercice.label}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Début</p>
                      <p className="text-lg font-bold">{exercice.date_debut}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Fin</p>
                      <p className="text-lg font-bold">{exercice.date_fin}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={exercice.is_cloture ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                      {exercice.is_cloture ? 'Clôturé' : 'Ouvert'}
                    </Badge>
                    <span className="text-sm text-slate-500">ID : {exercice.id?.slice(0, 8)}...</span>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Aucun exercice. Allez dans Comptabilité > Écritures pour en créer un.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== IA ===== */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle><Bot className="w-5 h-5 inline mr-2" />Configuration IA (structure)</CardTitle>
              <CardDescription>Override la configuration système. Laissez vide pour utiliser les paramètres par défaut du super admin.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Provider IA</Label>
                <Select value={aiConfig.ai_provider || ''} onValueChange={v => setAiConfig({...aiConfig, ai_provider: v})}>
                  <SelectTrigger><SelectValue placeholder="Par défaut (système)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude">Claude (Anthropic)</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Modèle</Label>
                <Select value={aiConfig.ai_model || ''} onValueChange={v => setAiConfig({...aiConfig, ai_model: v})}>
                  <SelectTrigger><SelectValue placeholder="Par défaut" /></SelectTrigger>
                  <SelectContent>
                    {aiConfig.ai_provider === 'claude' ? (<>
                      <SelectItem value="claude-sonnet-4-20250514">Claude Sonnet 4</SelectItem>
                      <SelectItem value="claude-opus-4-20250514">Claude Opus 4</SelectItem>
                      <SelectItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5</SelectItem>
                    </>) : aiConfig.ai_provider === 'openai' ? (<>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                      <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                    </>) : (
                      <SelectItem value="__none" disabled>Sélectionnez d'abord un provider</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Clé API (optionnel — override système)</Label>
                <Input type="password" value={aiConfig.ai_api_key || ''} onChange={e => setAiConfig({...aiConfig, ai_api_key: e.target.value})} placeholder={aiConfig.ai_api_key_configured ? 'Configurée' : 'Laisser vide pour utiliser la clé système'} />
              </div>
              <Button onClick={saveAi} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]"><Save className="w-4 h-4 mr-2" /> Enregistrer</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== CATÉGORIES ===== */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between">
            <p className="text-slate-500">Catégories utilisées pour classer les fournisseurs</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCatDialogOpen(true)}><Plus className="w-4 h-4 mr-2" /> Ajouter</Button>
              <Button onClick={saveCategories} className="bg-[#0F5156] hover:bg-[#0d464a]">Sauvegarder</Button>
            </div>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Label</TableHead><TableHead className="w-20">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{categories.map(c => (
              <TableRow key={c.code}><TableCell className="font-mono">{c.code}</TableCell><TableCell>{c.label}</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => removeCategory(c.code)}><Trash2 className="w-4 h-4 text-red-500" /></Button></TableCell>
              </TableRow>
            ))}{categories.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">Aucune catégorie</TableCell></TableRow>}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* ===== RÔLES & PERMISSIONS ===== */}
        <TabsContent value="roles"><Roles /></TabsContent>

        {/* ===== WORKFLOWS ===== */}
        <TabsContent value="workflows"><Workflows /></TabsContent>

        {/* ===== TEMPLATE FACTURE ===== */}
        <TabsContent value="template">
          <Card>
            <CardContent className="pt-6 text-center space-y-4">
              <FileText className="w-12 h-12 mx-auto text-[#0F5156] opacity-50" />
              <h3 className="text-lg font-bold">Éditeur visuel de template</h3>
              <p className="text-sm text-slate-500">Personnalisez l'apparence de vos factures avec un éditeur drag-and-drop.</p>
              <Button asChild className="bg-[#0F5156] hover:bg-[#0d464a]"><a href="/invoice-template">Ouvrir l'éditeur visuel</a></Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== AUDIT ===== */}
        <TabsContent value="audit"><AuditLog /></TabsContent>
      </Tabs>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prénom *</Label><Input value={userForm.prenom} onChange={e => setUserForm({...userForm, prenom: e.target.value})} /></div>
              <div><Label>Nom *</Label><Input value={userForm.nom} onChange={e => setUserForm({...userForm, nom: e.target.value})} /></div>
            </div>
            <div><Label>Email *</Label><Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} /></div>
            {!editingUser && <div><Label>Mot de passe *</Label><Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} /></div>}
            <div><Label>Rôle</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({...userForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roleOptions.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Gérez les rôles personnalisés dans Administration > Rôles & Permissions</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Annuler</Button>
            <Button onClick={saveUser} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>Nouvelle catégorie</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Code *</Label><Input value={newCat.code} onChange={e => setNewCat({...newCat, code: e.target.value})} placeholder="informatique" /></div>
            <div><Label>Label *</Label><Input value={newCat.label} onChange={e => setNewCat({...newCat, label: e.target.value})} placeholder="Informatique" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Annuler</Button>
            <Button onClick={addCategory} className="bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
